// ============================================================
// NavControls — Forward/back/play/flip buttons below the board
// ============================================================

import styles from "./NavControls.module.css"

interface NavControlsProps {
  onStepBack: () => void
  onStepForward: () => void
  onToggleAutoPlay: () => void
  onFlipBoard: () => void
  isAutoPlaying: boolean
  canStepBack: boolean
  canStepForward: boolean
  showKeyHints?: boolean
}

export const NavControls = ({
  onStepBack,
  onStepForward,
  onToggleAutoPlay,
  onFlipBoard,
  isAutoPlaying,
  canStepBack,
  canStepForward,
  showKeyHints = false,
}: NavControlsProps) => {
  return (
    <div className={styles.container}>
      {/* Flip board */}
      <div className={`${styles.buttonGroup} ${styles.flipGroup}`}>
        <button
          className={`${styles.controlButton} ${styles.flipButton}`}
          onClick={onFlipBoard}
          aria-label="Flip board"
          title="Flip board (F)"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 16V4m0 0L3 8m4-4l4 4" />
            <path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        </button>
        {showKeyHints && <kbd className={styles.keyHint}>F</kbd>}
      </div>

      {/* Step back */}
      <div className={styles.buttonGroup}>
        <button
          className={styles.controlButton}
          onClick={onStepBack}
          disabled={!canStepBack}
          aria-label="Previous move"
          title="Previous move (Left arrow)"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        {showKeyHints && <kbd className={styles.keyHint}>←</kbd>}
      </div>

      {/* Play/pause */}
      <div className={styles.buttonGroup}>
        <button
          className={`${styles.controlButton} ${styles.playButton}`}
          onClick={onToggleAutoPlay}
          aria-label={isAutoPlaying ? "Pause auto-play" : "Start auto-play"}
          title="Auto-play (Space)"
        >
          {isAutoPlaying ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="6,4 20,12 6,20" />
            </svg>
          )}
        </button>
        {showKeyHints && <kbd className={styles.keyHint}>Space</kbd>}
      </div>

      {/* Step forward */}
      <div className={styles.buttonGroup}>
        <button
          className={styles.controlButton}
          onClick={onStepForward}
          disabled={!canStepForward}
          aria-label="Next move"
          title="Next move (Right arrow)"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        {showKeyHints && <kbd className={styles.keyHint}>→</kbd>}
      </div>
    </div>
  )
}
